import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin } from "lucide-react";
import { useBrand } from "@/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  org_nr: "",
  firmanavn: "",
  kontaktperson: "",
  telefon: "",
  epost: "",
  melding: "",
};

const inputClass =
  "w-full px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:border-emerald-500 text-sm bg-white";
const labelClass = "block text-xs font-bold text-slate-700 uppercase mb-1";

const ContactSection = () => {
  const brand = useBrand();
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const update = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/henvendelser`, { ...form, brand: brand.id });
      toast.success("Takk for henvendelsen!", {
        description:
          "Vi har mottatt informasjonen din og tar kontakt med et tilbud tilpasset bedriften.",
      });
      setForm(initialForm);
    } catch (err) {
      toast.error("Noe gikk galt", {
        description:
          "Henvendelsen kunne ikke sendes. Prøv igjen, eller ring oss på 921 74 274.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" className="py-24 bg-[#0B132B] text-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 flex flex-col justify-between">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-emerald-400">
                Direkte kontakt
              </span>
              <h2 className="font-serif-heading text-3xl sm:text-4xl font-bold mt-2 mb-6">
                Ta kontakt med vårt kraftteam
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed mb-8">
                Ønsker bedriften en gjennomgang av eksisterende strømavtale eller
                et uforpliktende tilbud på Flexsikring? Ring oss
                eller fyll ut skjemaet.
              </p>

              <div className="space-y-4 mb-8 text-sm">
                <div className="flex items-center gap-4 p-4 rounded-lg bg-white/5 border border-white/10">
                  <div className="w-10 h-10 rounded bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                    <Phone className="w-5 h-5" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-400">Telefon</div>
                    <a
                      href="tel:+4792174274"
                      className="text-base font-bold text-white tracking-wide hover:text-emerald-400 transition-colors"
                      data-testid="contact-phone-link"
                    >
                      921 74 274
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 rounded-lg bg-white/5 border border-white/10">
                  <div className="w-10 h-10 rounded bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-400">E-post</div>
                    <a
                      href={`mailto:${brand.email}`}
                      className="text-base font-bold text-white hover:text-emerald-400 transition-colors"
                      data-testid="contact-email-link"
                    >
                      {brand.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 rounded-lg bg-white/5 border border-white/10">
                  <div className="w-10 h-10 rounded bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-400">Besøksadresse</div>
                    <div className="text-base font-bold text-white" data-testid="contact-address">
                      Selma Ellefsens vei 6, Oslo
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={`w-full h-40 rounded-xl bg-gradient-to-br ${brand.mapGradient} border border-white/10 p-4 flex flex-col justify-between`}>
              <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">
                Kartposisjon
              </span>
              <div className="text-xs text-slate-300">
                <span className="font-bold text-white block">{brand.companyLine}</span>
                Selma Ellefsens vei 6, 0581 Oslo
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 bg-white text-[#0F172A] p-8 sm:p-10 rounded-2xl shadow-2xl">
            <h3 className="text-2xl font-bold text-[#0B132B] mb-2">B2B Interesseskjema</h3>
            <p className="text-sm text-slate-600 mb-6">
              Fyll ut informasjonen under, så kontakter vi deg med et konkret
              tilbud tilpasset bedriftens forbruk.
            </p>

            <form className="space-y-4" onSubmit={handleSubmit} data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="org_nr" className={labelClass}>
                    Organisasjonsnummer
                  </label>
                  <input
                    id="org_nr"
                    type="text"
                    required
                    placeholder="F.eks. 987 654 321"
                    className={inputClass}
                    value={form.org_nr}
                    onChange={update("org_nr")}
                    data-testid="form-org-number-input"
                  />
                </div>
                <div>
                  <label htmlFor="firmanavn" className={labelClass}>
                    Firmanavn
                  </label>
                  <input
                    id="firmanavn"
                    type="text"
                    required
                    placeholder="Bedriftens navn AS"
                    className={inputClass}
                    value={form.firmanavn}
                    onChange={update("firmanavn")}
                    data-testid="form-company-input"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="kontaktperson" className={labelClass}>
                    Kontaktperson
                  </label>
                  <input
                    id="kontaktperson"
                    type="text"
                    required
                    placeholder="Navn Navnesen"
                    className={inputClass}
                    value={form.kontaktperson}
                    onChange={update("kontaktperson")}
                    data-testid="form-contact-person-input"
                  />
                </div>
                <div>
                  <label htmlFor="telefon" className={labelClass}>
                    Telefonnummer
                  </label>
                  <input
                    id="telefon"
                    type="tel"
                    required
                    placeholder="Ditt mobilnummer"
                    className={inputClass}
                    value={form.telefon}
                    onChange={update("telefon")}
                    data-testid="form-phone-input"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="epost" className={labelClass}>
                  E-postadresse
                </label>
                <input
                  id="epost"
                  type="email"
                  required
                  placeholder="navn@bedrift.no"
                  className={inputClass}
                  value={form.epost}
                  onChange={update("epost")}
                  data-testid="form-email-input"
                />
              </div>

              <div>
                <label htmlFor="melding" className={labelClass}>
                  Melding / estimert årlig forbruk (valgfritt)
                </label>
                <textarea
                  id="melding"
                  rows="3"
                  placeholder="Fortell gjerne litt om nåværende avtale eller estimert forbruk..."
                  className={inputClass}
                  value={form.melding}
                  onChange={update("melding")}
                  data-testid="form-message-input"
                />
              </div>

              <button
                type="submit"
                disabled={sending}
                className="w-full py-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-not-allowed text-[#0B132B] font-bold text-base transition-colors shadow-md"
                data-testid="form-submit-button"
              >
                {sending ? "Sender henvendelse..." : "Send inn uforpliktende henvendelse"}
              </button>

              <p className="text-[11px] text-slate-500 text-center">
                Vi behandler alle bedriftsopplysninger konfidensielt. Ingen
                binding ved innsending.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
